import React, { useEffect, useState } from "react";
import {
  Button,
  Container,
  Flex,
  SegmentedControl,
  Image,
} from "@mantine/core";
import { ImageWithBoundingBox } from "@labeling-tools/ui-components/src/ImageWithBoundingBox";
import {
  useNextPendingTask,
  useFetchTask,
  useImageGallery,
} from "@labeling-tools/api/lebron/react-query-hooks";
import { useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { LoadingOverlay } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { ResultsGrid } from "./ResultsGrid";
import { ProductSearch } from "./ProductSearch";
import { useEmbedding } from "@/hooks/useEmbedding";
import { TaskCounter } from "@labeling-tools/ui-components/src/TaskCounter";
import { ProductAssociationActions } from "./ProductAssociationActions";
import { JobTaskDetailSchema } from "@augmodo/lebron-browser-client";
import { useImageCrop } from "@labeling-tools/ui-components/src/hooks/useImageCrop";
import { ProductAssociationPlanogram } from "./ProductAssociationPlanogram";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setSelectedPlanogramId } from "@/store";
import { ProductAssociationQaActions } from "./ProductAssociationQaActions";
import { PlanogramModal } from "./PlanogramModal";
import {
  IdleTimeProvider,
  IdleWarningModal,
  ExitModal,
} from "@labeling-tools/ui-components/src";
import ProductAssociationImageChecks from "./ProductAssociationImageChecks";

interface TaskDetailProps {
  mode?: "admin" | "viewer" | "qa";
}

export const TaskDetailContent: React.FC<TaskDetailProps> = ({
  mode = "viewer",
}: TaskDetailProps) => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const taskId = searchParams.get("taskId");
  const { jobId } = useParams<{ jobId: string }>();
  const [selectedImageType, setSelectedImageType] = useState("Full Image");
  const [resultsView, setResultsView] = useState("Results");

  const planogramSelectedProductId = useAppSelector(
    (state) => state.productAssociation.planogramSelectedProductId,
  );
  // the fetch hook used depends on wether we're on an admin page or not
  const {
    data: nextPendingTask,
    isLoading: nextPendingTaskLoading,
    isFetching: nextPendingTaskFetching,
    isError: nextPendingTaskError,
  } = mode === "admin"
    ? useFetchTask(jobId, taskId)
    : useNextPendingTask(jobId);

  // fetch image gallery - main image
  const {
    mutate: mainImageMutation,
    data: mainImageGallery,
    isPending: mainImageLoading,
  } = useImageGallery();

  useEffect(() => {
    if (nextPendingTask?.data?.image_id) {
      // First mutation for main image
      mainImageMutation({
        image_ids: [nextPendingTask.data.image_id as string],
      });
    }
  }, [nextPendingTask?.data?.image_id, mainImageMutation]);

  // set planogram id to redux store if it exists in the task data or result
  useEffect(() => {
    if (
      nextPendingTask?.data?.planogram_id ||
      nextPendingTask?.result?.planogram_id
    ) {
      dispatch(
        setSelectedPlanogramId(
          nextPendingTask?.result?.planogram_id ||
            nextPendingTask?.data?.planogram_id ||
            null,
        ),
      );
    }
  }, [
    nextPendingTask?.data?.planogram_id,
    nextPendingTask?.result?.planogram_id,
    dispatch,
  ]);

  // if nextPendingTask errors (usually all tasks for a job are completed) return null
  useEffect(() => {
    if (nextPendingTaskError) {
      queryClient.setQueryData(["nextPendingTask", jobId], null);
    }
  }, [nextPendingTaskError]);

  const points = nextPendingTask?.data?.product_detections?.[0]?.points;

  const missingItemImageUrl = mainImageGallery?.images[0]?.image;
  const cropResult = useImageCrop(
    missingItemImageUrl || "",
    points,
    points,
    true,
  );
  const { embedding, loading: embeddingLoading } = useEmbedding(
    missingItemImageUrl || "",
    points,
  );

  if (mainImageLoading || nextPendingTaskLoading || nextPendingTaskFetching) {
    return (
      <div className="flex justify-center items-center">
        <LoadingOverlay visible={true} />
      </div>
    );
  }
  const noGallery = !mainImageLoading && !mainImageGallery;
  const noImagesFound = !missingItemImageUrl || !nextPendingTask;
  if (noGallery || noImagesFound) {
    return (
      <div className="flex flex-col justify-center items-center pt-10">
        <div className="mb-4 text-xl">No task found</div>
        <div>
          <Button
            onClick={() => {
              navigate("/");
            }}
          >
            Go to dashboard
          </Button>
        </div>
      </div>
    );
  }
  return (
    <>
      <Container
        size="xl"
        py="xl"
        w="100%"
        style={{ position: "relative", marginTop: 5 }}
      >
        {mode !== "admin" && (
          <TaskCounter taskId={nextPendingTask?.id as string} />
        )}
        <Flex gap="xl" pb={100}>
          {/* Main Image */}
          <div className="flex justify-center relative w-[600px] lg:w-[800px]">
            <div className="w-full max-w-md">
              <div>
                <SegmentedControl
                  data={["Full Image", "Cropped Image"]}
                  value={selectedImageType}
                  onChange={setSelectedImageType}
                  style={{ marginBottom: 10 }}
                />
                {selectedImageType === "Full Image" && (
                  <ImageWithBoundingBox
                    src={missingItemImageUrl}
                    variant="proposal"
                    alt="Target image"
                    points={[{ points: points as [number, number][] }]}
                    enableZoom={true}
                    initialZoom={3}
                    crop
                  />
                )}
                {selectedImageType === "Cropped Image" && (
                  <Image
                    src={cropResult?.croppedImageUrl}
                    alt="Cropped image"
                  />
                )}
              </div>
              <ProductAssociationImageChecks
                task={nextPendingTask as JobTaskDetailSchema}
              />
            </div>
          </div>

          <div className="relative w-full pl-[20px] lg:pl-[50px] border-l border-gray-200">
            <SegmentedControl
              data={["Results", "Planogram"]}
              value={resultsView}
              onChange={setResultsView}
              style={{ marginBottom: 10 }}
            />
            {resultsView === "Results" && (
              <div>
                <ProductSearch task={nextPendingTask} mode={mode} />
                <ResultsGrid
                  task={nextPendingTask as JobTaskDetailSchema}
                  embedding={embedding || []}
                  embeddingLoading={embeddingLoading}
                  mode={mode}
                />
              </div>
            )}
            {resultsView === "Planogram" && (
              <ProductAssociationPlanogram
                task={nextPendingTask as JobTaskDetailSchema}
              />
            )}
          </div>
        </Flex>
      </Container>
      {!!nextPendingTask && mode === "viewer" && (
        <ProductAssociationActions
          task={nextPendingTask as JobTaskDetailSchema}
        />
      )}
      {!!nextPendingTask && mode === "qa" && (
        <ProductAssociationQaActions
          task={nextPendingTask as JobTaskDetailSchema}
        />
      )}
      {!!nextPendingTask && (
        <PlanogramModal
          storeId={nextPendingTask?.data?.store_id || ""}
          task={nextPendingTask}
          planogramSelectedProductId={planogramSelectedProductId}
          capturedImage={
            <ImageWithBoundingBox
              src={missingItemImageUrl}
              variant="proposal"
              alt="Target image"
              points={[{ points: points as [number, number][] }]}
              enableZoom={true}
              initialZoom={3}
              crop
            />
          }
        />
      )}
      {!!nextPendingTask && (mode === "viewer" || mode === "qa") && (
        <IdleTimeProvider taskId={nextPendingTask?.id || ""}>
          <IdleWarningModal
            jobId={jobId || ""}
            taskId={nextPendingTask?.id || ""}
          />
          <ExitModal />
        </IdleTimeProvider>
      )}
    </>
  );
};
